// My Services → "Get ready for your contract": what a vendor must enter and upload before a
// services contract can be requested (checked again on the server, services/vendorReadiness.js).
// Step 1 the business (owner ID, a business document, the Vendor Agreement); step 2 each
// service's details, packages, photos and terms; step 3 request the contract.
// Once everything is ready, only the business documents stay (to add or replace a document).
import React, { useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Paper, Stack, Typography, Chip, Button, TextField, MenuItem, Alert, CircularProgress, Box, LinearProgress, Grid, Divider, Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { MY_VERIFICATION_QUERY } from '../graphql/queries';
import { uploadIdentityDocument } from '../utils/media';

const DOCS = {
  identity: [['aadhaar', 'Aadhaar card (mask the first 8 digits)'], ['pan', 'PAN card'], ['passport', 'Passport'], ['voter_id', 'Voter ID'], ['driving_licence', 'Driving licence']],
  business: [['gst_certificate', 'GST registration certificate'], ['trade_licence', 'Trade licence'], ['udyam', 'Udyam (MSME) certificate'],
    ['shop_establishment', 'Shop & Establishment registration'], ['fssai_licence', 'FSSAI licence (food businesses)'], ['business_other', 'Other business registration']],
};
const LABEL = Object.fromEntries([...DOCS.identity, ...DOCS.business, ['other', 'Other government ID']]);

function Check({ c }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" py={0.25}>
      {c.done ? <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.2 }} /> : <RadioButtonUncheckedIcon color="disabled" fontSize="small" sx={{ mt: 0.2 }} />}
      <Box>
        <Typography variant="body2" fontWeight={c.done ? 400 : 600}>{c.label}</Typography>
        {!c.done && c.hint && <Typography variant="caption" color="text.secondary">{c.hint}</Typography>}
      </Box>
    </Stack>
  );
}

// Uploads go to the private documents store; the admin checks them in Compliance → Verification
export function DocumentUpload({ vendorUserId, onUploaded }) {
  const context = vendorUserId ? { headers: { 'X-Vendor-User-Id': vendorUserId } } : undefined;
  const { data, refetch } = useQuery(MY_VERIFICATION_QUERY, { fetchPolicy: 'cache-and-network', context });
  const [purpose, setPurpose] = useState('business');
  const [docType, setDocType] = useState('gst_certificate');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileInput = useRef(null);
  const docs = data?.myVerification?.documents || [];

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      await uploadIdentityDocument(file, { docType, purpose, vendorUserId });
      setMsg({ severity: 'success', text: `${LABEL[docType]} uploaded. The admin will check it.` });
      refetch(); if (onUploaded) onUploaded();
    } catch (e) { setMsg({ severity: 'error', text: e.message }); }
    setBusy(false);
  };

  return (
    <Box mt={1}>
      {docs.length > 0 && (
        <Stack spacing={0.25} mb={1}>
          {docs.map(d => (
            <Typography key={d.id} variant="caption" display="block">
              {d.purpose === 'business' ? 'Business' : d.purpose === 'identity' ? 'Identity' : 'Address'}: {LABEL[d.docType] || d.docType} · {d.fileName}{' '}
              <Chip size="small" variant="outlined" label={d.status} color={d.status === 'accepted' ? 'success' : d.status === 'rejected' ? 'error' : 'default'} sx={{ height: 18 }} />
            </Typography>
          ))}
        </Stack>
      )}
      {msg && <Alert severity={msg.severity} sx={{ mb: 1 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label="Document for" value={purpose} sx={{ minWidth: 150 }}
          onChange={e => { setPurpose(e.target.value); setDocType(DOCS[e.target.value][0][0]); }}>
          <MenuItem value="business">Business</MenuItem>
          <MenuItem value="identity">Owner identity</MenuItem>
        </TextField>
        <TextField select size="small" label="Document" value={docType} onChange={e => setDocType(e.target.value)} sx={{ minWidth: 260 }}>
          {DOCS[purpose].map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
        </TextField>
        <Box>
          <input ref={fileInput} type="file" accept="application/pdf,.pdf,image/jpeg,image/png,image/webp" hidden onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
          <Button variant="outlined" size="small" disabled={busy} onClick={() => fileInput.current?.click()}
            startIcon={busy ? <CircularProgress size={14} /> : <UploadFileIcon />}>
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </Box>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>PDF or photo, up to 5 MB. Stored privately; only the admin's verification team sees them.</Typography>
    </Box>
  );
}

// canAdd: the documents are approved (or an admin is working for the vendor), so services can be added
export default function VendorReadinessPanel({ readiness, loading, vendorUserId, onRefresh, onAddDetails, onEdit, onRequestContract, onCsv, canAdd = true }) {
  const [open, setOpen] = useState(null); // expanded service category
  if (!readiness) return loading ? <LinearProgress sx={{ mb: 2 }} /> : null;
  const pending = readiness.services.filter(s => !s.contracted);
  const contracted = readiness.services.filter(s => s.contracted);
  // Once every service is in a contract and the business is done there is nothing to prepare,
  // but documents can still be added or replaced
  // (with no service yet, the full checklist stays so the first one can be added)
  if (readiness.businessReady && contracted.length && !pending.length && contracted.every(s => s.ready)) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Business documents</Typography>
        <Typography variant="body2" color="text.secondary">Add a new or renewed licence, registration or ID proof. The admin checks each one.</Typography>
        <DocumentUpload vendorUserId={vendorUserId} onUploaded={onRefresh} />
      </Paper>
    );
  }
  const pct = (checks) => Math.round(100 * checks.filter(c => c.done).length / Math.max(checks.length, 1));
  const readyToRequest = readiness.businessReady && pending.some(s => s.ready);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, borderColor: 'primary.light' }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
        <AssignmentTurnedInIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>Get ready for your contract</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Enter each service on the form or upload it from a CSV file (one template per service), then add photos and documents here.
        Everything below is needed first: a services contract can be requested only for services whose checklist is complete.
        Your details stay hidden from families until the contract is signed and accepted.
      </Typography>

      {!readiness.documentsApproved && (
        <Alert severity={canAdd ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {vendorUserId
            ? 'The owner ID and business document are not approved yet: review them in Admin → Vendors → Documents. As an admin you can still add services for this vendor.'
            : 'First upload the owner\'s identity proof and a business document below. You can add your services once the admin approves them; you will get a notification.'}
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" fontWeight={700}>1. Your business {readiness.businessReady && <Chip size="small" color="success" label="Done" sx={{ ml: 0.5 }} />}</Typography>
          {readiness.business.map(c => <Check key={c.key} c={c} />)}
          {readiness.business.some(c => c.key === 'agreement' && !c.done) && (
            <Typography variant="caption" display="block" sx={{ ml: 3.5 }}>
              <Link component={RouterLink} to="/legal/vendor-terms" target="_blank">Read the Vendor Agreement</Link>. You are asked to accept it when you sign in.
            </Typography>
          )}
          <DocumentUpload vendorUserId={vendorUserId} onUploaded={onRefresh} />
        </Grid>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" fontWeight={700} mb={0.5}>2. Each service you will offer</Typography>
          {!readiness.services.length && (
            <Alert severity="info" sx={{ mb: 1 }}>Add the details of a service you offer: its description, packages with prices, photos and cancellation terms.</Alert>
          )}
          {readiness.services.map(s => (
            <Paper key={s.category} variant="outlined" sx={{ p: 1.25, mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={600} sx={{ flex: 1 }}>{s.displayName}</Typography>
                {s.contracted && <Chip size="small" variant="outlined" label="In a contract" />}
                <Chip size="small" color={s.ready ? 'success' : 'warning'} label={s.ready ? 'Complete' : `${pct(s.checks)}%`} />
                <Button size="small" onClick={() => setOpen(open === s.category ? null : s.category)}>{open === s.category ? 'Hide' : 'Checklist'}</Button>
                {onCsv && <Button size="small" disabled={!canAdd} onClick={() => onCsv(s.category)}>CSV</Button>}
                {s.offeringId
                  ? <Button size="small" variant={s.ready ? 'text' : 'contained'} onClick={() => onEdit(s.offeringId)}>{s.ready ? 'Edit' : 'Complete'}</Button>
                  : <Button size="small" variant="contained" disabled={!canAdd} onClick={() => onAddDetails(s.category)}>Add details</Button>}
              </Stack>
              <LinearProgress variant="determinate" value={pct(s.checks)} color={s.ready ? 'success' : 'warning'} sx={{ mt: 0.75, height: 4, borderRadius: 2 }} />
              {open === s.category && <Box mt={1}>{s.checks.map(c => <Check key={c.key} c={c} />)}</Box>}
            </Paper>
          ))}
          <Stack direction="row" spacing={1}>
            <Button size="small" disabled={!canAdd} onClick={() => onAddDetails('')}>+ Add another service</Button>
            {onCsv && <Button size="small" disabled={!canAdd} onClick={() => onCsv('')}>+ Upload a service from CSV</Button>}
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          3. Request the contract{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            {readyToRequest ? `Ready: ${pending.filter(s => s.ready).map(s => s.displayName).join(', ')}`
              : !readiness.businessReady ? 'Finish step 1 first' : 'Complete a service in step 2 first'}
          </Typography>
        </Typography>
        <Button variant="contained" disabled={!readyToRequest} onClick={onRequestContract}>Request contract</Button>
      </Stack>
    </Paper>
  );
}
